// Session-randomised A/B assignment for the F1 "coins first" paywall test.
//
// Why a session and not a user: the brief's primary metric is checkout started
// per session that saw a paywall surface, and 77% of those sessions are
// anonymous, so a user id does not exist for most of the population. The unit
// that is randomised is the unit that is counted.
//
// The test row lives in website_ab_tests (test_name 'f1_coins_first'). Public
// read on that table was removed in 20251114000001, so the client asks the
// narrow `ab_test_allocation` RPC, which returns the treatment share for an
// ACTIVE test and null otherwise. No row, a paused row, a failed call or
// first-party analytics turned off all mean control for everyone: the test
// fails closed to today's order.
//
// The arm is pure arithmetic on (test name, unit), mirrored byte for byte in
// supabase/functions/_shared/abAssign.ts so the server can refuse a forged
// arm. _shared/__tests__/abImpressions.test.ts pins the two together. The unit
// is the analytics session id of the first enrolled tab (see the arm store).

import { useEffect, useSyncExternalStore } from "react";
import type { PaywallSurface, PaywallTrigger } from "@/lib/paywallAnalytics";
import { isTrackingEnabled } from "@/lib/tracking-toggles";

export const COINS_FIRST_TEST = "f1_coins_first";

/** A is control (today's order), B is treatment (coins first). */
export type AbArm = "A" | "B";
/**
 * "off" = not enrolled for this session: the test is inactive or unreachable,
 * or a tested surface was shown before the arm resolved. An off session sees
 * control and records no impression row, so it never enters either arm.
 */
type ArmState = AbArm | "off";

/** FNV-1a 32-bit, as a fraction in [0, 1). Same input, same bucket, forever. */
export function abBucket(testName: string, sessionId: string): number {
  const input = `${testName}:${sessionId}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash / 0x100000000;
}

/** `allocation` is the treatment share, as website_ab_tests.traffic_allocation. */
export function assignArm(testName: string, sessionId: string, allocation: number): AbArm {
  return abBucket(testName, sessionId) < allocation ? "B" : "A";
}

/**
 * The surfaces the treatment changes. Only these enrol a session, because a
 * session that only met, say, the post-generation Pro teaser saw the same thing
 * in both arms and would dilute the comparison toward zero.
 */
const CONTEXTUAL_TRIGGERS: ReadonlySet<PaywallTrigger> = new Set<PaywallTrigger>([
  "replace_track",
  "exclude_artist",
  "lock_track",
  "refine_locked",
]);

export function isCoinsFirstSurface(surface: PaywallSurface, trigger: PaywallTrigger): boolean {
  switch (surface) {
    case "limit_reached_dialog":
    case "refine_locked_wall":
    case "pricing_page":
      return true;
    case "upgrade_prompt_dialog":
      return CONTEXTUAL_TRIGGERS.has(trigger);
    default:
      return false;
  }
}

/** The fix-action and refine triggers whose dialog gets the contextual copy. */
export function isContextualTrigger(trigger: PaywallTrigger): boolean {
  return CONTEXTUAL_TRIGGERS.has(trigger);
}

// ── Arm store ──
//
// The arm lives in sessionStorage and never changes for the tab once set, so
// a surface can never render one arm and then the other.
//
// The randomisation UNIT is shared across tabs (F3). Email sign-in opens its
// link in a new tab, which starts a new analytics session, and keying the arm
// to that session redrew it mid-funnel: a visitor could see treatment, sign in,
// and check out under control. So the first enrolled tab writes its session id
// to localStorage as the unit, and any tab that resolves within UNIT_TTL_MS
// adopts it. Events carry it as `ab_unit` and the server recomputes the arm
// from it (supabase/functions/_shared/abImpressions.ts).
//
// The TTL is short on purpose: it covers a sign-in round trip, not a returning
// visitor, so the shared value is not a long-lived identifier.

const STORAGE_KEY = `ab_arm:${COINS_FIRST_TEST}`;
const UNIT_KEY = `ab_unit:${COINS_FIRST_TEST}`;
const SHARED_UNIT_KEY = `ab_unit_shared:${COINS_FIRST_TEST}`;
export const UNIT_TTL_MS = 24 * 60 * 60 * 1000;
/** The analytics session id shape (analytics.ts getSessionId). The server checks the same. */
export const AB_UNIT_PATTERN = /^\d{10,16}_[a-z0-9]{1,16}$/;

function readSession(key: string): string | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(key, value);
  } catch {
    /* private mode: the in-memory value still holds for this page */
  }
}

function readStored(): ArmState | null {
  const v = readSession(STORAGE_KEY);
  return v === "A" || v === "B" || v === "off" ? v : null;
}

function readStoredUnit(): string | null {
  const v = readSession(UNIT_KEY);
  return v && AB_UNIT_PATTERN.test(v) ? v : null;
}

/** The unit another tab enrolled with, if it is still fresh. */
function readSharedUnit(now: number): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(SHARED_UNIT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { unit, exp } = parsed as { unit?: unknown; exp?: unknown };
    if (typeof unit !== "string" || !AB_UNIT_PATTERN.test(unit)) return null;
    if (typeof exp !== "number" || exp <= now || exp > now + UNIT_TTL_MS) return null;
    return unit;
  } catch {
    return null;
  }
}

function writeSharedUnit(unit: string, now: number): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(SHARED_UNIT_KEY, JSON.stringify({ unit, exp: now + UNIT_TTL_MS }));
    }
  } catch {
    /* storage blocked: this tab still holds its own arm */
  }
}

let state: ArmState | null = readStored();
// Null for a tab enrolled before units existed: its events carry no ab_unit
// and the server falls back to the session id, which was the unit then.
let unit: string | null = readStoredUnit();
let loading = false;
const listeners = new Set<() => void>();

function settle(next: ArmState, nextUnit: string | null = null): void {
  if (state !== null) return;
  state = next;
  unit = next === "off" ? null : nextUnit;
  writeSession(STORAGE_KEY, next);
  if (unit) writeSession(UNIT_KEY, unit);
  listeners.forEach((l) => l());
}

/**
 * Resolve this tab's arm. Called at boot from AnalyticsInit and by a page that
 * is itself a tested surface. A treatment nobody can measure is not shown, so
 * first-party analytics turned off means off.
 */
export function primeCoinsFirstArm(): void {
  if (state !== null || loading) return;
  if (!isTrackingEnabled("first-party-analytics")) {
    settle("off");
    return;
  }
  loading = true;
  void (async () => {
    try {
      const [{ supabase }, { getAnalyticsSessionId }] = await Promise.all([
        import("@/integrations/supabase/client"),
        import("@/lib/analytics"),
      ]);
      const { data, error } = await supabase.rpc("ab_test_allocation", { p_test_name: COINS_FIRST_TEST });
      const sessionId = getAnalyticsSessionId();
      if (error || typeof data !== "number" || !sessionId) {
        settle("off");
        return;
      }
      // A page deadline already pinned this tab off: it must not hand its
      // session to other tabs as a unit it never enrolled with.
      if (state !== null) return;
      // Read after the RPC, so a tab opened in the same instant as another
      // still adopts that tab's unit if it landed first.
      const now = Date.now();
      const shared = readSharedUnit(now);
      const chosen = shared ?? sessionId;
      if (!shared && AB_UNIT_PATTERN.test(chosen)) writeSharedUnit(chosen, now);
      settle(assignArm(COINS_FIRST_TEST, chosen, data), AB_UNIT_PATTERN.test(chosen) ? chosen : null);
    } catch {
      settle("off");
    }
  })();
}

/**
 * Fix the arm at the moment a tested surface is shown. If it has not resolved
 * yet the session is "off": it saw control, so it must stay control rather
 * than flip to treatment when the RPC lands.
 */
function exposeCoinsFirst(): ArmState {
  if (state === null) settle("off");
  return state as ArmState;
}

/**
 * The value paywall events carry in `variant_id`, e.g. "f1_coins_first:B".
 * Null for an untested surface or an unenrolled session. Calling it for a
 * tested surface is the exposure, so it pins the arm.
 */
export function coinsFirstVariantId(surface: PaywallSurface, trigger: PaywallTrigger): string | null {
  if (!isCoinsFirstSurface(surface, trigger)) return null;
  const arm = exposeCoinsFirst();
  return arm === "off" ? null : `${COINS_FIRST_TEST}:${arm}`;
}

/**
 * The session's arm WITHOUT pinning it, for events that follow an exposure
 * rather than being one (a checkout start). Null when not enrolled.
 */
export function enrolledCoinsFirstVariantId(): string | null {
  return state === "A" || state === "B" ? `${COINS_FIRST_TEST}:${state}` : null;
}

/**
 * The unit the arm was drawn from, which events carry as `ab_unit`. Null when
 * not enrolled, and for a tab enrolled before units existed.
 */
export function coinsFirstUnitId(): string | null {
  return state === "A" || state === "B" ? unit : null;
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = () => state;
const serverSnapshot = () => null;

/**
 * True when this session is in the treatment arm. `shown` is whether the
 * caller's surface is on screen now: while it is, an unresolved arm is pinned
 * to control. Pass false for a closed dialog so merely mounting does not burn
 * the session's arm. A page that is a tested surface from first paint uses
 * useCoinsFirstPage instead.
 */
export function useCoinsFirst(shown: boolean): boolean {
  const current = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  useEffect(() => {
    if (shown) exposeCoinsFirst();
  }, [shown]);
  return current === "B";
}

/** How long a page waits for the arm before it shows control (F4). */
export const PAGE_ARM_DEADLINE_MS = 1500;

/**
 * For a page that is a tested surface from its first paint (/pricing). A
 * direct landing used to mount before AnalyticsInit had even asked for the arm,
 * so the page pinned itself off and those visitors never entered the test.
 *
 * Returns null while the arm resolves, so the page can hold its tested block
 * back instead of showing control and then switching. After
 * PAGE_ARM_DEADLINE_MS it pins whatever it has, which is off if nothing came.
 */
export function useCoinsFirstPage(): boolean | null {
  const current = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  useEffect(() => {
    primeCoinsFirstArm();
    if (state !== null) return;
    const timer = setTimeout(() => {
      exposeCoinsFirst();
    }, PAGE_ARM_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, []);
  return current === null ? null : current === "B";
}

/** Test-only: forget the in-memory arm and any in-flight resolution. */
export function __resetCoinsFirstForTests(next: ArmState | null = null, nextUnit: string | null = null): void {
  state = next;
  unit = nextUnit;
  loading = false;
}
